import { BrowserModule, Title } from '@angular/platform-browser';
import { inject, LOCALE_ID, NgModule } from '@angular/core';
import {
  TranslateModule,
  TranslateService,
  provideTranslateService,
} from '@ngx-translate/core';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { registerLocaleData } from '@angular/common';
import en from '@angular/common/locales/en';
import { ServiceWorkerModule } from '@angular/service-worker';
import { environment } from '../environments/environment';
import { SharedModule } from './@components/shared.module';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';
import { createClient } from 'graphql-ws';
import { provideHttpClient } from '@angular/common/http';
import {
  en_US,
  NZ_DATE_LOCALE,
  NZ_I18N,
} from 'ng-zorro-antd/i18n';
import { TimeagoModule } from 'ngx-timeago';
import { NzIconModule } from 'ng-zorro-antd/icon';
import {
  UserOutline,
  ContainerOutline,
  CustomerServiceOutline,
  GlobalOutline,
  BulbOutline,
  NotificationOutline,
  BankOutline,
  CarOutline,
  FundOutline,
  ControlOutline,
  PlusOutline,
  DashboardOutline,
  MenuFoldOutline,
  LogoutOutline,
  MenuUnfoldOutline,
  ArrowLeftOutline,
  DeleteOutline,
  PictureTwoTone,
  ExportOutline,
  InboxOutline,
  DownloadOutline,
  LockOutline,
  EyeInvisibleOutline,
  FieldTimeOutline,
  ScheduleOutline,
  FolderOpenOutline,
  AlertOutline,
  MoreOutline,
  DollarOutline,
} from '@ant-design/icons-angular/icons';
import { IconDefinition } from '@ant-design/icons-angular';
import {
  enUS,
} from 'date-fns/locale';
import { provideApollo } from 'apollo-angular';
import { HttpLink } from 'apollo-angular/http';
import { InMemoryCache } from '@apollo/client/cache';
import { ApolloLink, DefaultOptions, split } from '@apollo/client/core';
import { getMainDefinition } from '@apollo/client/utilities';
import { onError } from '@apollo/client/link/error';
import { setContext } from '@apollo/client/link/context';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';

const icons: IconDefinition[] = [
  UserOutline,
  ContainerOutline,
  CustomerServiceOutline,
  GlobalOutline,
  BulbOutline,
  NotificationOutline,
  BankOutline,
  CarOutline,
  FundOutline,
  ControlOutline,
  PlusOutline,
  DashboardOutline,
  MenuFoldOutline,
  MenuUnfoldOutline,
  LogoutOutline,
  ArrowLeftOutline,
  DeleteOutline,
  PictureTwoTone,
  ExportOutline,
  InboxOutline,
  DownloadOutline,
  LockOutline,
  MoreOutline,
  EyeInvisibleOutline,
  FieldTimeOutline,
  ScheduleOutline,
  FolderOpenOutline,
  DollarOutline,
  AlertOutline,
];
registerLocaleData(en);

@NgModule({
  declarations: [AppComponent],
  imports: [
    BrowserModule,
    AppRoutingModule,
    SharedModule,
    NzIconModule.forRoot(icons),
    BrowserAnimationsModule,
    TranslateModule.forRoot({
      fallbackLang: 'en',
      loader: provideTranslateHttpLoader({
        prefix: './assets/i18n/',
        suffix: '.json',
      }),
    }),
    ServiceWorkerModule.register('ngsw-worker.js', {
      enabled: environment.production,
    }),
    TimeagoModule.forRoot(),
  ],
  providers: [
    provideHttpClient(),
    provideTranslateService({
      loader: provideTranslateHttpLoader({
        prefix: './assets/i18n/',
        suffix: '.json',
      }),
    }),
    provideApollo(() => {
      const httpLink = inject(HttpLink);
      const http = httpLink.create({
        uri: `${environment.root}graphql`,
      });
      const wsLink = new GraphQLWsLink(
        createClient({
          url: environment.wsEndpoint,
          connectionParams: () => ({
            authToken: localStorage.getItem('ridy_admin_token'),
          }),
        }),
      );
      const basic = setContext(() => ({
        headers: {
          Accept: 'charset=utf-8',
        },
      }));
      const auth = setContext(() => {
        return {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('ridy_admin_token')}`,
          },
        };
      });
      const defaultOptions: DefaultOptions = {
        watchQuery: {
          fetchPolicy: 'no-cache',
          //errorPolicy: 'ignore',
        },
        query: {
          fetchPolicy: 'no-cache',
          //errorPolicy: 'all',
        },
      };
      const errorLink = onError(({ graphQLErrors, networkError }) => {
        if (graphQLErrors)
          graphQLErrors.forEach(({ message, locations, path }) =>
            console.log(
              `[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`,
            ),
          );
        if (networkError) console.log(`[Network error]: ${networkError}`);
      });

      const link = ApolloLink.from([basic, auth, errorLink, http]);
      const splitLink = split(
        ({ query }) => {
          const definition = getMainDefinition(query);
          return (
            definition.kind === 'OperationDefinition' &&
            definition.operation === 'subscription'
          );
        },
        wsLink,
        link,
      );

      return {
        cache: new InMemoryCache({
          addTypename: false,
        }),
        link: splitLink,

        defaultOptions,
      };
    }),
    {
      provide: NZ_DATE_LOCALE,
      useFactory: () => {
        // English only
        return enUS;
      },
    },
    {
      provide: NZ_I18N,
      useFactory: () => {
        // English only
        return en_US;
      },
      deps: [LOCALE_ID],
    },
  ],
  bootstrap: [AppComponent],
})
export class AppModule {
  constructor(
    private title: Title,
    private translator: TranslateService,
  ) {
    title.setTitle('Admin Panel');
  }
}
